/**
 * Edit Bill Line Page
 *
 * Dedicated page for editing a line item in a bill.
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Check, Loader2 } from 'lucide-react'

import api from '@/lib/api'
import { formatCurrency } from '@/lib/utils'
import { t } from '@/i18n'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { useToast } from '@/components/ui/toaster'
import { Skeleton } from '@/components/ui/skeleton'

interface BillLine {
  id: number
  article_id: number
  article_code: string
  article_description: string
  quantity: number
  unit_price: number
  discount_percent: number
  subtotal: number
  tax_rate: number
  total: number
}

interface Bill {
  id: number
  status: string
  lines: BillLine[]
}

export function EditLinePage() {
  const { id, lineId } = useParams<{ id: string; lineId: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  // Fetch bill data
  const { data: bill, isLoading } = useQuery({
    queryKey: ['bill', id],
    queryFn: async () => {
      const response = await api.get(`/bills/${id}`)
      return response.data as Bill
    },
    enabled: !!id,
  })

  // Find the line
  const line = bill?.lines.find(l => l.id === parseInt(lineId || '0'))

  // Form state
  const [quantity, setQuantity] = useState(1)
  const [unitPrice, setUnitPrice] = useState(0)
  const [discountPercent, setDiscountPercent] = useState(0)

  // Initialize when line loads
  useEffect(() => {
    if (line) {
      setQuantity(line.quantity)
      setUnitPrice(line.unit_price)
      setDiscountPercent(line.discount_percent)
    }
  }, [line])

  // Calculate estimated total using the article's real IVA rate (from the
  // line) instead of hardcoding 13%, so exempt items (0%) and reduced rates
  // (1/2/4%) preview correctly. The authoritative recalculation happens
  // server-side on save.
  const calculateEstimatedTotal = () => {
    const grossSubtotal = quantity * unitPrice
    const discount = grossSubtotal * (discountPercent / 100)
    const afterDiscount = grossSubtotal - discount
    const tax = afterDiscount * ((line?.tax_rate ?? 0) / 100)
    return afterDiscount + tax
  }

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async () => {
      const response = await api.put(`/bills/${id}/lines/${lineId}`, {
        quantity,
        unit_price: unitPrice,
        discount_percent: discountPercent,
      })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bill', id] })
      toast({ title: t('pos.lineUpdated'), variant: 'success' })
      navigate(`/bills/new?continue=${id}`)
    },
    onError: (error: any) => {
      toast({
        title: t('common.error'),
        description: error.response?.data?.detail || t('pos.updateError'),
        variant: 'destructive',
      })
    },
  })

  const handleSubmit = () => {
    updateMutation.mutate()
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!bill || !line) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <p className="text-muted-foreground">{t('pos.lineNotFound')}</p>
        <Button variant="outline" onClick={() => navigate(`/bills/new?continue=${id}`)} className="mt-4">
          {t('common.back')}
        </Button>
      </div>
    )
  }

  // Redirect if bill is finalized
  if (bill.status === 'finalized') {
    navigate(`/bills/${id}`)
    return null
  }

  return (
    <div className="space-y-4 pb-24">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(`/bills/new?continue=${id}`)}
          className="shrink-0"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-xl md:text-2xl font-bold">
            {t('pos.editLine')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {line.article_code}
          </p>
        </div>
      </div>

      {/* Article Info */}
      <Card>
        <CardHeader>
          <CardTitle>{line.article_code}</CardTitle>
          <CardDescription>{line.article_description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Quantity */}
          <div className="space-y-2">
            <Label>{t('pos.quantity')}</Label>
            <Input
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
              className="h-14 text-xl text-center"
            />
          </div>

          {/* Unit Price (read-only — el precio no se edita desde aquí) */}
          <div className="space-y-2">
            <Label>{t('pos.unitPrice')}</Label>
            <div className="h-14 flex items-center justify-center rounded-md border border-input bg-muted text-xl font-medium">
              {formatCurrency(unitPrice)}
            </div>
          </div>

          {/* Discount */}
          <div className="space-y-2">
            <Label>{t('pos.discountPercent')}</Label>
            <Input
              type="number"
              min="0"
              max="100"
              value={discountPercent}
              onChange={(e) => setDiscountPercent(parseFloat(e.target.value) || 0)}
              className="h-14 text-xl text-center"
            />
          </div>

          {/* Estimated Total */}
          <div className="p-4 bg-primary/10 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">{t('pos.estimatedTotal')}:</span>
              <span className="text-2xl font-bold text-primary">
                {formatCurrency(calculateEstimatedTotal())}
              </span>
            </div>
            {discountPercent > 0 && (
              <p className="text-sm text-green-600 text-right mt-1">
                -{discountPercent}% descuento aplicado
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Fixed Footer Actions */}
      <div className="fixed bottom-16 left-0 right-0 p-4 bg-background border-t z-50 md:relative md:bottom-0 md:border-0 md:p-0 md:bg-transparent md:z-auto">
        <div className="flex gap-2 max-w-screen-lg mx-auto">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/bills/new?continue=${id}`)}
            className="flex-1 h-12"
          >
            {t('pos.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={updateMutation.isPending}
            className="flex-1 h-12"
          >
            {updateMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            {t('pos.save')}
          </Button>
        </div>
      </div>
    </div>
  )
}
